import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { FlagGroupService } from '../../flag/flag.group.service';
import { FlagService } from '../../flag/flag.service';
import { SettingService } from '../../setting/setting.service';
import { Answer, Flag, FlagGroup } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { AnswerService } from '../answer.service';
import { InspectionService } from '../inspection.service';

@Component({
  selector: 'app-edit-flag',
  templateUrl: './edit.flag.component.html',
  styleUrls: ['./edit.flag.component.scss'],
})
export class EditFlagComponent implements OnInit {
  @ViewChild('editFlagModal', { static: true }) public editFlagModal: ModalDirective;
  @ViewChild('ngSelectComponent', { static: true }) ngSelectComponent: any;

  changedCallback: (f) => void;
  deletedCallback: () => void;
  flag: Flag;
  answer: Answer;
  editFlagForm: UntypedFormGroup;
  scoresEnabled = true;
  hideDelete = false;
  canUngroup = false;

  saving = false;

  clientX: number;
  clientY: number;
  modalStyle;

  hideLegacy = false;
  lockTypeAndGroup = false;

  flagTypes = [];
  allFlagGroups: FlagGroup[] = [];

  groups = [];
  showViewMore = false;
  searched = false;
  showOnlyAutoFlagTags = true;
  autoFlagTagsMapped = [];
  flagManagerMapped = [];
  autoFlagTagsAndFlagGroups = [];
  inspectionGroups = [];

  get flagSubtypes() {
    const flagType = this.editFlagForm.get('flagType').value;
    if (flagType) {
      return flagType.subtypes;
    } else {
      return [];
    }
  }

  constructor(
    public answerService: AnswerService,
    public userService: UserService,
    public flagService: FlagService,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private dialog: DialogService,
    public settingService: SettingService,
    public inspectionService: InspectionService,
    private flagGroupService: FlagGroupService,
    private el: ElementRef,
  ) {}

  async ngOnInit() {
    this.editFlagModal.onHidden.subscribe(x => {
      if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
        this.cancel();
      }
    });

    this.editFlagForm = this.fb.group({
      flagType: '',
      flagSubtype: '',
      text: '',
      score: 0,
      group: [null, [this.groupNameValidator.bind(this)]],
    });

    const subtypeInfo = await this.flagService.subtypeInfo;
    this.flagTypes = await this.flagService.listTypes();
    this.flagTypes.forEach(type => {
      type.subtypes.forEach(subtype => {
        subtype.name = this.flagService.getSubtypeInfo(subtypeInfo, subtype.name).name;
      });
    });

    this.editFlagForm.get('flagType').valueChanges.subscribe(type => {
      if (type) {
        this.editFlagForm.get('flagSubtype').setValue(type ? type.subtypes[0] : null);
      }
    });

    this.editFlagForm.get('flagType').setValue(this.flagTypes[0]);

    this.editFlagForm.get('flagSubtype').valueChanges.subscribe(async () => {
      await this.loadGroups();
      this.editFlagForm.get('group').updateValueAndValidity();
    });

    this.scoresEnabled = await this.settingService.scoresEnabled();

    this.canUngroup = await this.userService.hasPermissions([permissions.FLAG.MANAGER]);
  }

  groupNameValidator(control: AbstractControl): { [key: string]: any } | null {
    if (this.editFlagForm) {
      const groupName = control.value || '';
      const groupExistWithOtherSubtype = this.allFlagGroups.find(
        g =>
          g.name.toUpperCase() === groupName.toUpperCase() &&
          g.subtypeId &&
          g.subtypeId !== this.editFlagForm.get('flagSubtype').value.id,
      );
      if (groupExistWithOtherSubtype) {
        return { flagGroupExistsWithOtherSubtype: { value: control.value } };
      }
    }
    return null;
  }

  async addTagFn(name) {
    return { id: name, name: name, tag: true };
  }

  async loadInspectionGroups() {
    const [allFlagManagerGroupTags] = await this.flagGroupService.listByInspection(this.answer.inspectionId);
    this.inspectionGroups = allFlagManagerGroupTags;
  }

  async loadGroups() {
    // Flag Groups
    const allFlagManagerGroupTags = [...this.inspectionGroups];
    this.allFlagGroups = allFlagManagerGroupTags;
    this.flagManagerMapped = allFlagManagerGroupTags.map(fm => ({ id: fm.id, name: fm.name, type: 'flag' }));
    this.flagManagerMapped.sort((a, b) => a.name.localeCompare(b.name));

    // Auto Flag Tags
    this.autoFlagTagsMapped = this.answer.question.flagGroupTags.map(qt => ({
      id: qt.id,
      name: qt.name,
      type: 'question',
    }));
    this.autoFlagTagsMapped.sort((a, b) => a.name.localeCompare(b.name));

    this.evaluateGroups();
  }

  evaluateGroups() {
    // Check if we should show only Auto Flag Tags
    this.showOnlyAutoFlagTags = this.autoFlagTagsMapped.length > 0;

    // Combine both groups
    this.autoFlagTagsAndFlagGroups = [...this.autoFlagTagsMapped, ...this.flagManagerMapped];
    const uniqueGroups = this.filterUniqueGroups(this.autoFlagTagsAndFlagGroups);

    if (this.showOnlyAutoFlagTags) {
      // auto flag tags
      this.groups = uniqueGroups.filter(group => group.type === 'question');

      // Check for flag groups with type 'flag'
      const hasFlagTypeGroups = uniqueGroups.some(group => group.type === 'flag');
      if (hasFlagTypeGroups) {
        this.groups.push({ id: null, name: 'View more', type: 'viewMore', class: 'view-more-style' });
      }
    } else if (this.flagManagerMapped.length > 0) {
      // flag groups
      this.groups = uniqueGroups.filter(group => group.type === 'flag');
    } else {
      this.groups = [];
    }
  }

  filterUniqueGroups(allGroups: any[]): any[] {
    const uniqueGroups = [];
    const addedGroupNames = new Set();

    allGroups.forEach(group => {
      if (!addedGroupNames.has(group.name) && this.groupNameValidator({ value: group.name } as any) === null) {
        uniqueGroups.push(group);
        addedGroupNames.add(group.name);
      }
    });
    return uniqueGroups;
  }

  onGroupSelected(selectedGroup: any) {
    if (selectedGroup?.type === 'viewMore') {
      this.onViewMoreClick();
      setTimeout(() => {
        this.ngSelectComponent.open();
      }, 0);
      this.editFlagForm.get('group').reset();
    }
  }

  onViewMoreClick() {
    this.showViewMore = true;
    const uniqueAutoFlagGroups = this.filterUniqueGroups(this.autoFlagTagsMapped);

    // Filter flag groups with the same name as autoflag groups (question config)
    const filteredFlagManagerMapped = this.flagManagerMapped.filter(flagGroup => {
      return !uniqueAutoFlagGroups.some(autoFlagGroup => autoFlagGroup.name === flagGroup.name);
    });
    this.groups = [...uniqueAutoFlagGroups, ...this.filterUniqueGroups(filteredFlagManagerMapped)];
  }

  onSearch(event: any) {
    if (event.term) {
      this.autoFlagTagsAndFlagGroups = [...this.autoFlagTagsMapped, ...this.flagManagerMapped];
      this.groups = this.filterUniqueGroups(this.autoFlagTagsAndFlagGroups);
      this.searched = true;
    } else {
      this.showViewMore = false;
      this.evaluateGroups();
    }
  }

  onClearSearch() {
    if (this.searched) {
      this.searched = false;
    }
    this.showViewMore = true;
    this.evaluateGroups();
  }

  async show(options: {
    changed?: (f) => void;
    deleted?: () => void;
    flag: Flag;
    answer?: Answer;
    coords?: any;
    hideDelete?: boolean;
    lockTypeAndGroup?: boolean;
  }) {
    this.changedCallback = options.changed;
    this.deletedCallback = options.deleted;
    this.flag = await this.flagService.get(options.flag.id);
    this.answer = options.answer;
    this.clientX = options.coords && options.coords.clientX;
    this.clientY = options.coords && options.coords.clientY;
    this.hideDelete = options.hideDelete;
    this.lockTypeAndGroup = !!options.lockTypeAndGroup;
    this.setTypeAndGroupFieldsEnabled(!this.lockTypeAndGroup);

    if (this.flagTypes && options.flag && options.flag.subtype && options.flag.subtype.type) {
      this.editFlagForm.get('flagType').setValue(this.flagTypes.find(ft => ft.id === options.flag.subtype.type.id));
      this.editFlagForm.get('flagSubtype').setValue(this.flagSubtypes.find(fst => fst.id === options.flag.subtype.id));
      this.hideLegacy = options.flag.subtype.name !== 'Legacy';
    }

    this.editFlagForm.get('text').setValue(this.flag.text);
    this.editFlagForm.get('score').setValue(this.flag.score);

    if (this.answer) {
      await this.loadInspectionGroups();
      await this.loadGroups();
    }

    if (this.flag.groupId) {
      const selectedFlagGroup = this.allFlagGroups.find(fg => fg.id === this.flag.groupId);
      if (selectedFlagGroup) {
        this.editFlagForm.get('group').setValue(selectedFlagGroup.name);
      }
    } else {
      this.editFlagForm.get('group').setValue(null);
    }

    this.setPosition();
    this.editFlagModal.show();
  }

  setPosition() {
    if (typeof this.clientX !== 'number') {
      this.modalStyle = {};
      return;
    }

    const wrapperDiv = this.el.nativeElement.getElementsByClassName('modal')[0] as HTMLDivElement;
    const div = this.el.nativeElement.getElementsByClassName('modal-dialog')[0] as HTMLDivElement;

    wrapperDiv.style.display = 'block';
    const width = div.offsetWidth;
    const height = div.offsetHeight;
    wrapperDiv.style.display = 'none';
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;

    let left = this.clientX + 30;
    const fitsX = screenWidth > width + this.clientX + 30;
    if (!fitsX) {
      left = this.clientX - width - 30;
    }

    const topAdjustment = Math.min(0, screenHeight - (height + this.clientY));

    this.modalStyle = {
      position: 'absolute',
      left: `${left}px`,
      top: `${this.clientY - 30 + topAdjustment}px`,
    };
  }

  clickUnlink() {
    this.dialog.confirm().show({
      message: `Do you really want to unlink the Picture from the Flag?`,
      confirmed: async () => {
        await this.flagService.edit(this.flag.id, {
          ...this.flag,
          picture: null,
          pictureId: null,
          y: null,
          x: null,
        });
        const newFlag = await this.flagService.get(this.flag.id);
        this.flag.picture = null;
        this.flag.y = null;
        this.flag.x = null;

        this.toastr.success('Flag unlinked');
        this.editFlagModal.hide();
        return this.changedCallback && this.changedCallback(newFlag);
      },
    });
  }

  populateGroupWithFlagData(group) {
    group.description = this.flag.description;
    group.violation = this.flag.violation;
    group.discrepancy = this.flag.discrepancy;
  }

  populateFlagWithGroupData(group) {
    this.flag.description = group.description;
    this.flag.violation = group.violation;
    this.flag.discrepancy = group.discrepancy;
  }

  async createFlagGroup(name, formData) {
    return await this.flagGroupService.create(this.answer.inspectionId, {
      name: name,
      subtypeId: formData.flagSubtype.id,
      description: this.flag.description,
      violation: this.flag.violation,
      discrepancy: this.flag.discrepancy,
    });
  }

  async onSave() {
    if (this.saving) {
      return;
    }

    if (this.editFlagForm.valid) {
      this.saving = true;
      const formData = this.editFlagForm.value;

      if (this.lockTypeAndGroup) {
        await this.save(formData, this.flag.groupId);
        this.saving = false;
        return;
      }

      const groupValue = this.editFlagForm.get('group').value;

      let flagGroupId = null;
      let needsConfirmation = false;
      if (groupValue && groupValue !== '') {
        const group = this.groups.find(g => g.name === groupValue);
        if (group) {
          if (group.type === 'flag') {
            // Check if this flag group exists with same name and without subtype
            const groupExistWithoutSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === null,
            );
            // Check if this flag group exists with same name and subtype
            const groupExistWithSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === formData.flagSubtype.id,
            );

            if (groupExistWithSubtype) {
              flagGroupId = groupExistWithSubtype.id;
              if (groupExistWithSubtype.flags.length > 0) {
                needsConfirmation = true;
                await this.dialog.confirm().show({
                  message: `The flag-level information will be deleted and replace by the group-level information, do you want to continue?.`,
                  confirmed: async () => {
                    this.populateFlagWithGroupData(groupExistWithSubtype);
                    await this.save(formData, flagGroupId);
                    await this.flagService.addToGroup(flagGroupId, this.flag.id);
                    return;
                  },
                  canceled: async () => {
                    this.saving = false;
                    return;
                  },
                });
              } else {
                this.populateGroupWithFlagData(groupExistWithSubtype);
                await this.flagGroupService.edit(flagGroupId, groupExistWithSubtype);
              }
            } else if (groupExistWithoutSubtype) {
              flagGroupId = groupExistWithoutSubtype.id;
              // Change the subtype of the flag group
              groupExistWithoutSubtype.subtypeId = formData.flagSubtype.id;
              this.populateGroupWithFlagData(groupExistWithoutSubtype);
              await this.flagGroupService.edit(flagGroupId, groupExistWithoutSubtype);
            } else {
              // If the group with subtype doesn't exist, create it
              const flagGroup = await this.createFlagGroup(group.name, formData);
              flagGroupId = flagGroup.id;
            }
          } else if (group.type === 'question') {
            // Check if this flag group exists with same name and without subtype
            const groupExistWithSubtype = this.allFlagGroups.find(
              g => g.name === group.name && g.subtypeId === formData.flagSubtype.id,
            );
            // If the group with subtype doesn't exist, create it
            if (!groupExistWithSubtype) {
              const flagGroup = await this.flagGroupService.create(this.answer.inspectionId, {
                name: group.name,
                subtypeId: formData.flagSubtype.id,
              });
              flagGroupId = flagGroup.id;
            } else {
              flagGroupId = groupExistWithSubtype.id;
            }
          }
        } else {
          const flagGroup = await this.createFlagGroup(groupValue, formData);
          flagGroupId = flagGroup.id;
        }
      }

      if (!needsConfirmation) {
        await this.save(formData, flagGroupId);
        this.saving = false;
      }
    }
  }

  async save(formData, flagGroupId) {
    const updatedAnswer = await this.flagService.edit(this.flag.id, {
      ...this.flag,
      subtype: undefined,
      subtypeId: this.lockTypeAndGroup ? this.flag.subtypeId : formData.flagSubtype.id,
      text: formData.text,
      y: this.flag.y,
      x: this.flag.x,
      score: formData.score,
      groupId: this.lockTypeAndGroup ? this.flag.groupId : flagGroupId,
    });
    this.editFlagModal.hide();
    this.setTypeAndGroupFieldsEnabled(true);
    this.lockTypeAndGroup = false;
    const newFlag = await this.flagService.get(this.flag.id);
    this.answer.pictures.map(p => {
      const updatedPicture = updatedAnswer.pictures.find(up => up.id === p.id);
      if (updatedPicture) {
        p.updatedAt = updatedPicture.updatedAt;
      }
    });
    this.saving = false;
    return this.changedCallback && this.changedCallback(newFlag);
  }

  async remove() {
    this.dialog.confirm().show({
      message: `Do you really want to remove the Flag?`,
      confirmed: async () => {
        const updatedAnswer = await this.flagService.remove(this.flag.id);
        this.answer.pictures.map(p => {
          const updatedPicture = updatedAnswer.pictures.find(up => up.id === p.id);
          if (updatedPicture) {
            p.updatedAt = updatedPicture.updatedAt;
          }
        });
        this.editFlagModal.hide();
        this.setTypeAndGroupFieldsEnabled(true);
        this.lockTypeAndGroup = false;
        return this.deletedCallback && this.deletedCallback();
      },
    });
  }

  cancel() {
    this.setTypeAndGroupFieldsEnabled(true);
    this.lockTypeAndGroup = false;
    this.editFlagModal.hide();
  }

  private setTypeAndGroupFieldsEnabled(enabled: boolean): void {
    const action = enabled ? 'enable' : 'disable';
    this.editFlagForm.get('flagType')?.[action]({ emitEvent: false });
    this.editFlagForm.get('flagSubtype')?.[action]({ emitEvent: false });
    this.editFlagForm.get('group')?.[action]({ emitEvent: false });
  }

  getValidationClass(field: string) {
    const control = this.editFlagForm.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }
}
